import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createLandingCategory,
  deleteLandingCategory,
  getAdminLandingTree,
  updateLandingCategory,
  type BulletItem,
  type CreateCategoryBody,
  type ImageItem,
  type LandingTree,
  type ParagraphItem,
  type UpdateCategoryBody,
} from "../api/landingContent";

const KEY = "landing-content";

/** Full admin landing-content tree (§L — HTTP via TanStack Query). */
export function useLandingContentTree() {
  return useQuery<LandingTree>({
    queryKey: [KEY, "admin-tree"],
    queryFn: () => getAdminLandingTree(false),
    staleTime: 30_000,
  });
}

/** Per-category children that travel together with the category save. */
export interface CategoryChildren {
  paragraphs: ParagraphItem[];
  bullets: BulletItem[];
  images: ImageItem[];
}

/**
 * Save one complete category aggregate in a single API request. The backend
 * persists parent/children transactionally and records one aggregate changelog
 * entry. `mode` decides POST vs PUT; `key` is the path id.
 */
export function useSaveLandingCategory() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    {
      mode: "add" | "edit";
      key: string;
      category: CreateCategoryBody | UpdateCategoryBody;
      children: CategoryChildren;
    }
  >({
    mutationFn: async ({ mode, key, category, children }) => {
      if (mode === "add") {
        await createLandingCategory({
          ...(category as CreateCategoryBody),
          ...children,
        });
      } else {
        await updateLandingCategory(key, {
          ...(category as UpdateCategoryBody),
          ...children,
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** Cascade soft-delete a category. */
export function useDeleteLandingCategory() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (key) => deleteLandingCategory(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
